import { generateKeywordsQueryForOr } from '@smarttransit/common'
import { searchTransportationProfiles } from '../../services'
import { addAlert } from '../../utilities/helpers'

export default {
  name: 'bus-selector',
  props: {
    value: {
      type: Object,
      default: null
    },
    label: {
      type: String,
      default: 'Search bus'
    },
    placeholder: {
      type: String,
      default: 'Filter by licence plate or shortcode'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    showRecentWhenEmpty: {
      type: Boolean,
      default: false
    },
    recentProfilesLimit: {
      type: Number,
      default: 20
    },
    searchDelay: {
      type: Number,
      default: 800
    },
    onSelectedBus: {
      type: Function,
      default: null
    }
  },
  data () {
    return {
      isLoading: false,
      searchKey: '',
      internalValue: null,
      transportationProfiles: [],
      searchHandle: null,
      suppressNextEmit: false
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler (newValue) {
        const resolvedValue = newValue || null
        if (resolvedValue !== this.internalValue) {
          this.suppressNextEmit = true
          this.internalValue = resolvedValue
        }

        if (newValue) {
          this.addSelectedProfilesToOptions([newValue])
        }
      }
    },
    internalValue (newValue) {
      if (this.suppressNextEmit) {
        this.suppressNextEmit = false
        return
      }

      const selected = newValue || null
      this.$emit('input', selected)

      if (this.$props.onSelectedBus) {
        this.$props.onSelectedBus({
          transportationProfile: selected
        })
      }
    },
    searchKey (newValue) {
      this.runSearch(newValue)
    }
  },
  mounted () {
    this.runSearch(this.searchKey)
  },
  beforeDestroy () {
    clearTimeout(this.searchHandle)
  },
  methods: {
    formatSelectionLabel (profile) {
      if (!profile) {
        return ''
      }

      return profile.licensePlateNumber || profile.shortcodeId || profile.id || ''
    },
    formatItemSubtitle (profile) {
      if (!profile || !profile.shortcodeId) {
        return ''
      }

      if (profile.licensePlateNumber && profile.shortcodeId !== profile.licensePlateNumber) {
        return profile.shortcodeId
      }

      return ''
    },
    getSelectedProfiles () {
      return this.internalValue ? [this.internalValue] : []
    },
    addSelectedProfilesToOptions (profiles) {
      const selectedProfiles = Array.isArray(profiles) ? profiles.filter((item) => item && item.id) : []
      if (!selectedProfiles.length) {
        return
      }

      const options = this.transportationProfiles.slice()
      selectedProfiles.forEach((profile) => {
        const exists = options.find((item) => item.id === profile.id)
        if (!exists) {
          options.unshift(profile)
        }
      })

      this.transportationProfiles = options
    },
    buildFilter (keywords) {
      const filter = {
        limit: !keywords && this.showRecentWhenEmpty ? (this.recentProfilesLimit || 20) : 20
      }

      if (!keywords && this.showRecentWhenEmpty) {
        filter.order = 'dateCreated DESC'
      }

      if (keywords) {
        const keywordFilter = generateKeywordsQueryForOr(keywords, ['licensePlateNumber', 'shortcodeId'])
        if (keywordFilter && keywordFilter.length) {
          filter.where = {
            or: keywordFilter
          }
        }
      }

      return filter
    },
    runSearch (keywords) {
      clearTimeout(this.searchHandle)
      this.searchHandle = setTimeout(() => {
        this.searchTransportationProfiles(keywords)
      }, this.searchDelay || 800)
    },
    getErrorMessage (err, fallback) {
      if (err && err.error && err.error.message) {
        return err.error.message
      }
      if (err && err.message) {
        return err.message
      }
      return fallback
    },
    async searchTransportationProfiles (keywords) {
      this.isLoading = true
      try {
        const trimmedKeywords = String(keywords || '').trim()
        if (!trimmedKeywords && !this.showRecentWhenEmpty) {
          this.transportationProfiles = this.getSelectedProfiles()
          return
        }

        const filter = this.buildFilter(trimmedKeywords)
        const profiles = await searchTransportationProfiles(filter)
        const mappedProfiles = profiles || []

        if (!trimmedKeywords) {
          const selectedProfiles = this.getSelectedProfiles()
          const selectedIdMap = {}
          selectedProfiles.forEach((profile) => {
            selectedIdMap[profile.id] = true
          })

          this.transportationProfiles = selectedProfiles.concat(
            mappedProfiles.filter((profile) => !selectedIdMap[profile.id])
          )
        } else {
          this.transportationProfiles = mappedProfiles
          this.addSelectedProfilesToOptions(this.getSelectedProfiles())
        }
      } catch (err) {
        addAlert({
          message: `Error in bus search: ${this.getErrorMessage(err, 'Unknown error')}`,
          type: 'error'
        })
      } finally {
        this.isLoading = false
      }
    }
  }
}
